import {
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { CellContext } from '@tanstack/react-table';
import Brazil from 'innovate/assets/flags/Brazil.svg';
import SA from 'innovate/assets/flags/SouthAfrica.svg';
import uk from 'innovate/assets/flags/Uk flag innobrew.png';
import vector from 'innovate/assets/images/select_all.png';
import shareImage from 'innovate/assets/images/share.svg';
import MultiSelectInput from 'innovate/components/Inputs/MultiSelectInput';
import Mexico from 'innovate/assets/flags/Mexico.svg';
import China from 'innovate/assets/flags/china.png';
import Colombia from 'innovate/assets/flags/colombia.png';
import Peru from 'innovate/assets/flags/peru.svg';
import Ecuador from 'innovate/assets/flags/ecudor.svg';
import DominicanRepublic from 'innovate/assets/flags/dominican-republic-flag-icon.png';

import { FilterType, ScenarioType, SharePopoverProps, SortOrder } from 'innovate/utils/types';
import React from 'react';
import { BsStarFill, BsStar } from 'react-icons/bs';
import { FaCheck, FaSortAmountUp, FaSortAmountDown } from 'react-icons/fa';
import { FiSearch } from 'react-icons/fi';
import { MdFilterList } from 'react-icons/md';
import { TbArrowsSort } from 'react-icons/tb';

// Reusable Filter Header Props
interface FilterHeaderProps {
  title: string;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  sortValue: string | null;
  setSortValue: (value: string | null) => void;
  sortAscKey: string;
  sortDescKey: string;
  sortAscLabel?: string;
  sortDescLabel?: string;
  searchPlaceholder?: string;
}

// Reusable Filter Header Component with Search and A-Z/Z-A Sort
const FilterHeader: React.FC<FilterHeaderProps> = ({
  title,
  searchTerm,
  setSearchTerm,
  sortValue,
  setSortValue,
  sortAscKey,
  sortDescKey,
  sortAscLabel = 'a to z',
  sortDescLabel = 'z to a',
  searchPlaceholder = 'Search...',
}) => {
  const hasSearch = searchTerm.trim().length > 0;
  const hasSort = sortValue === sortAscKey || sortValue === sortDescKey;
  const isActive = hasSearch || hasSort;

  const getItemStyle = (isItemActive: boolean) => ({
    bg: isItemActive ? 'yellow.100' : 'transparent',
    fontWeight: isItemActive ? '700' : '500',
  });

  return (
    <Flex alignItems="center" justifyContent="center" gap={1}>
      <Text color="#636363D6" fontSize="11px" fontFamily="Avantt bold">
        {title}
      </Text>
      <Menu isLazy closeOnSelect={false}>
        <Tooltip label="Search & Sort">
          <MenuButton
            as={IconButton}
            aria-label={`Search ${title}`}
            icon={<MdFilterList size={12} />}
            size="xs"
            variant="ghost"
            color={isActive ? '#E3B100' : '#636363D6'}
            _hover={{ bg: 'gray.100' }}
          />
        </Tooltip>
        <MenuList
          fontSize="12px"
          sx={{ minWidth: '170px', width: '190px' }}
          p={2}
          boxShadow="0px 0px 20px 0px #0000001F"
        >
          <Box px={2} pb={2}>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <FiSearch color="#636363D6" />
              </InputLeftElement>
              <Input
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                borderRadius="6px"
                fontSize="12px"
              />
            </InputGroup>
          </Box>
          <Box borderTop="1px solid #eee" my={1} />
          <Text fontSize="11px" color="gray.500" px={3} mb={1}>
            Sort
          </Text>
          <MenuItem
            icon={<FaSortAmountUp color="#000000" />}
            onClick={() => setSortValue(sortAscKey)}
            {...getItemStyle(sortValue === sortAscKey)}
          >
            <Flex justify="space-between" w="100%" fontSize="11px" color="#000000" fontWeight="600">
              {sortAscLabel}
              {sortValue === sortAscKey && <FaCheck />}
            </Flex>
          </MenuItem>
          <MenuItem
            icon={<FaSortAmountDown color="#000000" />}
            onClick={() => setSortValue(sortDescKey)}
            {...getItemStyle(sortValue === sortDescKey)}
          >
            <Flex justify="space-between" w="100%" fontSize="11px" color="#000000" fontWeight="600">
              {sortDescLabel}
              {sortValue === sortDescKey && <FaCheck />}
            </Flex>
          </MenuItem>
          {isActive && (
            <>
              <Box borderTop="1px solid #eee" my={1} />
              <MenuItem
                onClick={() => {
                  setSearchTerm('');
                  setSortValue(null);
                }}
                fontSize="11px"
                color="gray.500"
              >
                Clear All
              </MenuItem>
            </>
          )}
        </MenuList>
      </Menu>
    </Flex>
  );
};

// MultiSelect Filter Header Props
interface MultiSelectFilterHeaderProps {
  title: string;
  options: { label: string; value: string }[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  searchPlaceholder?: string;
}

// MultiSelect Filter Header Component with Search and Multi-selection
const MultiSelectFilterHeader: React.FC<MultiSelectFilterHeaderProps> = ({
  title,
  options,
  selectedValues,
  onSelectionChange,
  searchPlaceholder = 'Search...',
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const isActive = selectedValues.length > 0;

  // Check if all options are selected
  const allSelected = options.length > 0 && selectedValues.length === options.length;

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    let result = options;

    // Filter by search term
    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter((opt) => opt.label.toLowerCase().includes(lowerSearch));
    }

    // Sort alphabetically by label (A to Z)
    return result.sort((a, b) => a.label.localeCompare(b.label));
  }, [options, searchTerm]);

  const handleToggle = (value: string) => {
    if (value === 'ALL') {
      // Toggle: If all options are selected, clear all. Otherwise, select all.
      if (allSelected) {
        onSelectionChange([]);
        setSearchTerm('');
      } else {
        onSelectionChange(options.map((opt) => opt.value));
      }
    } else if (selectedValues.includes(value)) {
      // Deselect option
      onSelectionChange(selectedValues.filter((v) => v !== value));
    } else {
      // Select option
      onSelectionChange([...selectedValues, value]);
    }
  };

  return (
    <Flex alignItems="center" justifyContent="center" gap={1}>
      <Text color="#636363D6" fontSize="11px" fontFamily="Avantt bold">
        {title}
      </Text>
      <Menu isLazy closeOnSelect={false}>
        <Tooltip label="Filter">
          <MenuButton
            as={IconButton}
            aria-label={`Filter ${title}`}
            icon={<MdFilterList size={12} />}
            size="xs"
            variant="ghost"
            color={isActive ? '#E3B100' : '#636363D6'}
            _hover={{ bg: 'gray.100' }}
          />
        </Tooltip>
        <MenuList fontSize="9px" minW="200px" mt={-1} boxShadow="0px 0px 20px 0px #0000001F">
          <InputGroup size="sm" mb={2} px={3}>
            <InputLeftElement pointerEvents="none" h="28px">
              <SearchIcon color="gray.400" w={3} h={3} ml={4} />
            </InputLeftElement>
            <Input
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              borderRadius="md"
              fontSize="11px"
              h="28px"
              pl={8}
              fontWeight="600"
            />
          </InputGroup>

          {/* Scrollable options container */}
          <Box maxH="200px" overflowY="auto">
            {/* ALL Option */}
            <MenuItem
              onClick={() => handleToggle('ALL')}
              bg={
                allSelected
                  ? 'linear-gradient(rgba(245, 224, 3, 0.5), rgba(229, 182, 17, 0.5))'
                  : 'transparent'
              }
              _hover={{ bg: '#FFF1C2' }}
              fontWeight="600"
            >
              <Text textTransform="uppercase">All</Text>
            </MenuItem>

            {filteredOptions.length === 0 ? (
              <MenuItem isDisabled fontWeight="600" color="gray.500">
                No options
              </MenuItem>
            ) : (
              filteredOptions.map((option) => {
                // Show as selected if this specific option is in selectedValues
                const isSelected = selectedValues.includes(option.value);
                return (
                  <MenuItem
                    key={option.value}
                    onClick={() => handleToggle(option.value)}
                    bg={
                      isSelected
                        ? 'linear-gradient(rgba(245, 224, 3, 0.5), rgba(229, 182, 17, 0.5))'
                        : 'transparent'
                    }
                    _hover={{ bg: '#FFF1C2' }}
                  >
                    <Text fontWeight="600" textTransform="uppercase">
                      {option.label}
                    </Text>
                  </MenuItem>
                );
              })
            )}
          </Box>
        </MenuList>
      </Menu>
    </Flex>
  );
};

// Date Sort Header Props
interface DateSortHeaderProps {
  sortValue: string | null;
  setSortValue: (value: string | null) => void;
}

// Date Sort Header Component (Latest/Oldest only)
const DateSortHeader: React.FC<DateSortHeaderProps> = ({ sortValue, setSortValue }) => {
  const hasSort = sortValue === 'latest' || sortValue === 'oldest';

  const getItemStyle = (isItemActive: boolean) => ({
    bg: isItemActive ? 'yellow.100' : 'transparent',
    fontWeight: isItemActive ? '700' : '500',
  });

  return (
    <Flex alignItems="center" justifyContent="center" gap={1}>
      <Text color="#636363D6" fontSize="11px" fontFamily="Avantt bold">
        Created On
      </Text>
      <Menu>
        <Tooltip label="Sort by date">
          <MenuButton
            as={IconButton}
            aria-label="Sort by date"
            icon={<MdFilterList size={12} />}
            size="xs"
            variant="ghost"
            color={hasSort ? '#E3B100' : '#636363D6'}
            _hover={{ bg: 'gray.100' }}
          />
        </Tooltip>
        <MenuList fontSize="12px" minW="140px" boxShadow="0px 0px 20px 0px #0000001F">
          <MenuItem
            icon={<FaSortAmountDown color="#000000" />}
            onClick={() => setSortValue('latest')}
            {...getItemStyle(sortValue === 'latest')}
          >
            <Flex justify="space-between" w="100%">
              Latest First
              {sortValue === 'latest' && <FaCheck />}
            </Flex>
          </MenuItem>
          <MenuItem
            icon={<FaSortAmountUp color="#000000" />}
            onClick={() => setSortValue('oldest')}
            {...getItemStyle(sortValue === 'oldest')}
          >
            <Flex justify="space-between" w="100%">
              Oldest First
              {sortValue === 'oldest' && <FaCheck />}
            </Flex>
          </MenuItem>
          {hasSort && (
            <>
              <Box borderTop="1px solid #eee" my={1} />
              <MenuItem onClick={() => setSortValue(null)} fontSize="11px" color="gray.500">
                Clear Sort
              </MenuItem>
            </>
          )}
        </MenuList>
      </Menu>
    </Flex>
  );
};

// Reusable Sortable Header Props
interface SortableHeaderProps {
  title: string;
  sortKey: string;
  currentSort: { key: string | null; direction: 'asc' | 'desc' } | null;
  onSortChange: (key: string, direction: 'asc' | 'desc' | null) => void;
  tooltipAsc?: string;
  tooltipDesc?: string;
}

/**
 * Reusable Sortable Header Component
 * Displays a single clickable sort icon that toggles between ascending and descending
 * Can be used for dates, strings, and numbers
 */
const SortableHeader: React.FC<SortableHeaderProps> = ({
  title,
  sortKey,
  currentSort,
  onSortChange,
  tooltipAsc = 'Sort ascending',
  tooltipDesc = 'Sort descending',
}) => {
  const isActive = currentSort?.key === sortKey;
  const currentDirection = isActive ? currentSort.direction : 'asc';

  const handleClick = () => {
    if (!isActive) {
      // First click: sort ascending
      onSortChange(sortKey, 'asc');
    } else if (currentDirection === 'asc') {
      // Second click: sort descending
      onSortChange(sortKey, 'desc');
    } else {
      // Third click: clear sort
      onSortChange(sortKey, null);
    }
  };

  // Determine tooltip label
  let tooltipLabel = tooltipAsc;
  if (isActive) {
    tooltipLabel = currentDirection === 'asc' ? tooltipDesc : 'Clear sort';
  }

  // Choose icon based on state
  const getIcon = () => {
    if (!isActive) {
      return <TbArrowsSort size={14} />;
    }
    return currentDirection === 'asc' ? (
      <FaSortAmountUp size={12} />
    ) : (
      <FaSortAmountDown size={12} />
    );
  };

  return (
    <Flex alignItems="center" justifyContent="center" gap={1}>
      <Text color="#636363D6" fontSize="11px" fontFamily="Avantt bold">
        {title}
      </Text>
      <Tooltip label={tooltipLabel}>
        <IconButton
          aria-label={`Sort ${title}`}
          icon={getIcon()}
          size="xs"
          variant="ghost"
          color={isActive ? '#E3B100' : '#636363D6'}
          _hover={{ bg: 'gray.100' }}
          onClick={handleClick}
        />
      </Tooltip>
    </Flex>
  );
};

interface PinnedHeaderProps {
  activeFilter: FilterType;
  sortOrder: SortOrder | null;
  onFilterChange: (type: FilterType) => void;
  onSortChange: (order: SortOrder | null) => void;
}

const CheckboxHeader = () => <Image src={vector} m={1} />;

const PinnedHeader: React.FC<PinnedHeaderProps> = ({
  activeFilter,
  sortOrder,
  onFilterChange,
  onSortChange,
}) => {
  const getItemStyle = (isActive: boolean) => ({
    bg: isActive ? 'yellow.100' : 'transparent',
    fontWeight: isActive ? '700' : '500',
  });

  return (
    <Menu offset={[0, 6]}>
      <Tooltip label="Filter pinned items">
        <MenuButton
          as={IconButton}
          aria-label="Pinned filter"
          icon={<BsStarFill color={activeFilter === 'pinned' ? '#00A884' : '#bbb'} />}
          size="xs"
          variant="ghost"
        />
      </Tooltip>

      <MenuList fontSize="12px" minW="160px" boxShadow="0px 0px 20px 0px #0000001F">
        {/* Filter Options */}
        <MenuItem onClick={() => onFilterChange('all')} {...getItemStyle(activeFilter === 'all')}>
          <Flex justify="space-between" w="100%">
            Show All
            {activeFilter === 'all' && <FaCheck />}
          </Flex>
        </MenuItem>

        <MenuItem
          icon={<BsStarFill color="#00A884" />}
          onClick={() => onFilterChange('pinned')}
          {...getItemStyle(activeFilter === 'pinned')}
        >
          <Flex justify="space-between" w="100%">
            Pinned
            {activeFilter === 'pinned' && <FaCheck />}
          </Flex>
        </MenuItem>

        <MenuItem
          icon={<BsStar />}
          onClick={() => onFilterChange('unpinned')}
          {...getItemStyle(activeFilter === 'unpinned')}
        >
          <Flex justify="space-between" w="100%">
            Unpinned
            {activeFilter === 'unpinned' && <FaCheck />}
          </Flex>
        </MenuItem>

        {/* Sorting Section ONLY when All selected */}
        {activeFilter === 'all' && (
          <>
            <Box borderTop="1px solid #eee" my={1} />

            <MenuItem
              icon={<FaSortAmountUp color="#000000" />}
              onClick={() => onSortChange('pinned_first')}
              {...getItemStyle(sortOrder === 'pinned_first')}
            >
              <Flex justify="space-between" w="100%">
                Pinned First
                {sortOrder === 'pinned_first' && <FaCheck />}
              </Flex>
            </MenuItem>

            <MenuItem
              icon={<FaSortAmountDown color="#000000" />}
              onClick={() => onSortChange('unpinned_first')}
              {...getItemStyle(sortOrder === 'unpinned_first')}
            >
              <Flex justify="space-between" w="100%">
                Unpinned First
                {sortOrder === 'unpinned_first' && <FaCheck />}
              </Flex>
            </MenuItem>
          </>
        )}
      </MenuList>
    </Menu>
  );
};

const IncMaCoHeader = () => (
  <Box className="header-content incremental-maco-header">Incremental MaCo (&apos;000 USD)</Box>
);

const formatDate: React.FC<CellContext<ScenarioType, unknown>> = ({ getValue }) => {
  const value = getValue();

  // Handle null, undefined, or empty string
  if (!value) {
    return <p>-</p>;
  }

  // You may need to assert the value type if you are sure it's a date string or timestamp
  const date = new Date(value as string | number);

  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  };

  return <p>{date.toLocaleDateString('en-GB', options).replaceAll(' ', '-')}</p>;
};

// Helper to get country flag and title
const COUNTRY_CONFIG = [
  {
    keywords: ['south africa'],
    imageUrl: SA,
    title: 'South Africa',
  },
  {
    keywords: ['brazil'],
    imageUrl: Brazil,
    title: 'Brazil',
  },
  {
    keywords: ['uk'],
    imageUrl: uk,
    title: 'United Kingdom',
  },
  {
    keywords: ['mex'],
    imageUrl: Mexico,
    title: 'Mexico',
  },
  {
    keywords: ['col', 'colombia'],
    imageUrl: Colombia,
    title: 'Colombia',
  },
  {
    keywords: ['cnn'],
    imageUrl: China,
    title: 'China_STW',
  },
  {
    keywords: ['china', 'chn'],
    imageUrl: China,
    title: 'CHINA_NIELSEN',
  },
  {
    keywords: ['per'],
    imageUrl: Peru,
    title: 'Peru',
  },
  {
    keywords: ['ecu'],
    imageUrl: Ecuador,
    title: 'Ecuador',
  },
  {
    keywords: ['dr', 'dominican'],
    imageUrl: DominicanRepublic,
    title: 'Dominican Republic',
  },
];

const getCountryDetails = (countryValue: string): { imageUrl: string; title: string } => {
  const value = countryValue.toLowerCase();

  const match = COUNTRY_CONFIG.find(({ keywords }) =>
    keywords.some((keyword) => value.includes(keyword))
  );

  return match ? { imageUrl: match.imageUrl, title: match.title } : { imageUrl: '', title: '' };
};

const TableCell: React.FC<CellContext<ScenarioType, unknown>> = ({ getValue, column }) => {
  const value = getValue() as ScenarioType[keyof ScenarioType];
  const header = column?.columnDef?.header;
  const columnId = column?.id;

  // Handle volume columns
  if (header === 'Total Volume' || header === 'Incremental Volume') {
    return <Text>{value} HL</Text>;
  }

  // Handle country column
  if (header === 'Country' || columnId === 'Country') {
    if (typeof value !== 'string') {
      return <Text>{value}</Text>;
    }

    const { imageUrl, title } = getCountryDetails(value);

    if (!imageUrl) {
      return <Text>{value}</Text>;
    }

    return (
      <Center>
        <Tooltip label={title} hasArrow>
          <Image boxSize="33px" src={imageUrl} alt={title} borderRadius="full" objectFit="cover" />
        </Tooltip>
      </Center>
    );
  }

  return <Text>{value}</Text>;
};

const SharePopover: React.FC<SharePopoverProps> = ({
  isOpen,
  onClose,
  userOptions,
  userValue,
  handleSelectChange,
  handleShare,
  openShareList,
  rowData,
  hideTrigger = false,
  isLoading = false,
}) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Element;
      // Ignore clicks inside this popover wrapper
      if (wrapperRef.current?.contains(target)) return;
      // Ignore clicks inside chakra-react-select portal menus (rendered outside DOM tree)
      if (target.closest?.("[class*='chakra-react-select']")) return;
      onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen, onClose]);

  return (
    <div ref={wrapperRef} style={{ display: hideTrigger ? 'inline-block' : 'contents' }}>
      <Popover isOpen={isOpen} onClose={onClose} closeOnBlur={false} closeOnEsc={true}>
        <PopoverTrigger>
          {hideTrigger ? (
            <Box as="span" position="absolute" visibility="hidden" pointerEvents="none" />
          ) : (
            <IconButton
              pl="12px"
              title="Share the simulation"
              aria-label="share"
              icon={<Image src={shareImage} alt="share" />}
              onClick={() => {
                openShareList(rowData);
              }}
              bg="none"
              _hover={{ bg: 'none' }}
              _active={{ bg: 'none' }}
              minW="auto"
              h="auto"
            />
          )}
        </PopoverTrigger>
        {isOpen && (
          <PopoverContent w="290px">
            <PopoverBody p={4}>
              <Flex mb={2} alignItems="center" justifyContent="center">
                <Image src={shareImage} />
                <Text color="black" ml={2}>
                  Share Scenario
                </Text>
              </Flex>

              <Flex alignItems="center" justifyContent="center" gap={1}>
                <Box flex={1}>
                  <MultiSelectInput
                    name="shareUser"
                    id="shareUser"
                    options={userOptions}
                    value={userValue}
                    onChange={(selectedOptions) => handleSelectChange(selectedOptions)}
                    placeholder="Select User"
                  />
                </Box>
                <Button
                  variant="outline"
                  bg="#191919"
                  color="#FFFFFF"
                  minW="85px"
                  isDisabled={userValue.length === 0 || isLoading}
                  isLoading={isLoading}
                  loadingText="Sharing"
                  spinnerPlacement="start"
                  spinner={<Spinner size="sm" color="#E3B100" />}
                  onClick={() => {
                    handleShare();
                  }}
                >
                  Share
                </Button>
              </Flex>
            </PopoverBody>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
};

const updateSimulation = (
  setSimulations: React.Dispatch<React.SetStateAction<ScenarioType[]>>,
  updatedSimulation: ScenarioType
) => {
  setSimulations((prevSimulations) =>
    prevSimulations.map((simulation) =>
      simulation.id === updatedSimulation.id
        ? { ...simulation, name: updatedSimulation.name }
        : simulation
    )
  );
};

const buildPinnedTooltipContent = (productAttributes?: any) => {
  if (!productAttributes) return 'This scenario is pinned';

  const fieldMappings = [
    { key: 'P_Brand', label: 'Brand' },
    { key: 'P_Subbrand', label: 'Sub-brand' },
    { key: 'P_Flavour', label: 'Flavour' },
    { key: 'P_Category', label: 'Category' },
    { key: 'P_SubCategory', label: 'Sub Category' },
    { key: 'P_Sub_Category', label: 'Sub Category' },
    { key: 'P_Beer_Type', label: 'Beer Type' },
    { key: 'P_Pack_Quantity', label: 'Pack Quantity' },
    { key: 'P_Price_Segment', label: 'Price Segment' },
    { key: 'P_Pack_Return_Type', label: 'Pack Type' },
  ];

  return (
    <Box>
      <Flex
        alignItems="center"
        gap={2}
        mb={3}
        pb={2}
        borderBottom="2px solid"
        borderColor="rgba(244, 224, 15, 0.3)"
      >
        <Box
          bg="linear-gradient(135deg, #F4E00F 0%, #C19600 100%)"
          borderRadius="full"
          w="20px"
          h="20px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="#141414"
          fontSize="11px"
          fontWeight="bold"
        >
          ★
        </Box>
        <Text fontWeight="700" fontSize="14px" color="#141414">
          Pinned Scenario
        </Text>
      </Flex>

      <Box>
        {fieldMappings.map(({ key, label }) => {
          const value = productAttributes[key];
          if (!value) return null;
          return (
            <Flex
              key={key}
              mb={2}
              py={1}
              px={2}
              borderRadius="4px"
              _hover={{ bg: 'rgba(244, 224, 15, 0.08)' }}
            >
              <Text
                fontSize="11px"
                fontWeight="600"
                color="#4b4b4bff"
                minWidth="100px"
                textTransform="uppercase"
              >
                {label} :
              </Text>
              <Text fontSize="12px" fontWeight="600" color="#181818ff" flex="1">
                {value}
              </Text>
            </Flex>
          );
        })}
      </Box>
    </Box>
  );
};

export {
  CheckboxHeader,
  PinnedHeader,
  FilterHeader,
  MultiSelectFilterHeader,
  DateSortHeader,
  SortableHeader,
  IncMaCoHeader,
  TableCell,
  formatDate,
  SharePopover,
  updateSimulation,
  buildPinnedTooltipContent,
  COUNTRY_CONFIG,
};
